import Button from 'tio-ui/components/button';
import { fn } from '@ember/helper';
import { not } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { Section } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import AppContentCard from 'tio-common/components/app/content-card';
import Component from '@glimmer/component';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import ErrorMessages from 'tio-ui/components/error-messages';
import edjsHTML from 'editorjs-html';
import { saveRecord } from 'tio-data/requests/save-record';
import type { ErrorsSignature } from 'tio-ui/components/error-messages';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { MatchPlanExtension as MatchPlanModel } from 'tio-data/data/resources/match-plan.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type RouterService from 'tio-employee/services/router';
import type { Store } from '@warp-drive/core';
import Checkbox from 'tio-ui/components/checkbox';
import { htmlSafe } from '@ember/template';

interface SyfEnrollmentConfirmationSignature {
  Args: {
    person: PersonModel;
    matchPlan: MatchPlanModel;
    onConfirmation: () => void;
    onCancel: () => void;
    loading: boolean;
    errors: ErrorsSignature | string | undefined;
  };
}

class SyfEnrollmentConfirmationComponent extends Component<SyfEnrollmentConfirmationSignature> {
  @service declare router: RouterService;
  @service declare store: Store;

  get canEnroll() {
    // separate getter here since this will theoretically involve
    // more conditions than just this component state (ie actual)
    // terms and conditions
    // TODO: verify that this is actually doing what it's supposed
    // to under the hood
    return this.args.person.accounts?.every((account) => !account.isLoading) ?? false;
  }

  // TODO: throttle this
  toggleBorrowerConfirmed = (account: AccountModel) => {
    // NB: expecting this to mean something beyond just this local
    // component state at some point in the future
    account.isVerified = !account.isVerified;
    saveRecord(this.store, account);
  };

  onAddStudentLoan = () => {
    this.router.transitionTo('authenticated.observability.upload', {
      queryParams: { source: 'syf', providers: 'statement,nslds' },
    });
  };

  isFormattedMatchPlan(str: string) {
    try {
      const parsed = JSON.parse(str);
      return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed);
    } catch {
      return false;
    }
  }

  formatMatchStatement = (matchStatement: string) => {
    if (this.isFormattedMatchPlan(matchStatement)) {
      const edjsParser = edjsHTML();
      const parsed = JSON.parse(matchStatement);
      return edjsParser.parse(parsed);
    } else {
      return matchStatement;
    }
  };

  get showErrors() {
    return !!this.args.errors;
  }

  <template>
    <h2 class="text-midnight font-semibold mb-4">{{t "syf.account_confirmation.header"}}</h2>
    <h3 class="font-semibold mb-2">{{t "syf.account_confirmation.subheader"}}</h3>
    <div class="ml-4 syf-match-statement">{{htmlSafe
        (this.formatMatchStatement @matchPlan.matchStatement)
      }}</div>
    <div class="ml-4 mb-8">
      {{@matchPlan.matchFrequencyDescription}}
    </div>
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 mb-12 gap-8">
      {{#each @person.activeAccounts as |account|}}
        <AppContentCard @titleClass="mb-6" class="col-span-1">
          <div class="font-semibold mb-3">
            {{t "account_linking.service_provider"}}:
            {{account.institutionName}}
          </div>
          <Section class="m:h-48 mb-6">
            <:body>
              <dl class="grid grid-cols-2 mb-4 text-sm">
                <dt class="mb-7 mr-4">{{t "syf.account_confirmation.borrower_name_label"}}</dt>
                <dd class="mb-7 font-semibold">{{account.observation.pii.name}}</dd>
                <dt class="mb-7 mr-4">{{t
                    "syf.account_confirmation.expected_monthly_payment_label"
                  }}</dt>
                <dd class="mb-7 font-semibold">
                  {{safeFormatNumber
                    account.providerRegularMonthlyPayment
                    style="currency"
                    currency="USD"
                  }}
                </dd>
              </dl>
            </:body>
          </Section>
          <div>
            <p class="mb-6">
              {{t "syf.account_confirmation.confirm_text"}}
            </p>
          </div>
          <div>
            <Checkbox
              data-test-id="account-confirmed-checkbox"
              @checked={{account.isVerified}}
              @onChange={{fn this.toggleBorrowerConfirmed account}}
              class="mb-8 font-semibold"
              @label={{@matchPlan.ensureVerifyAccountStatement}}
            />
          </div>
        </AppContentCard>
      {{/each}}
    </div>

    <section>
      <ErrorMessages @showErrors={{this.showErrors}} @error={{@errors}} class="text-center mb-8" />
      <div class="tio-row-loose flex-col-reverse md:flex-row md:justify-center">
        <Button {{on "click" @onCancel}} @appearance="outlined" @intent="danger" class="mx-8 w-48">
          {{t "syf.account_confirmation.cancel_button"}}
        </Button>
        <Button
          @isDisabled={{not this.canEnroll}}
          @isRunning={{@loading}}
          @intent="primary"
          {{on "click" @onConfirmation}}
          class="mx-8 w-48"
        >
          {{t "syf.account_confirmation.enroll_button"}}
        </Button>
      </div>
    </section>
  </template>
}

export default SyfEnrollmentConfirmationComponent;
